import React, { useRef, useEffect, useCallback } from 'react';
import { formatTime } from '../utils/timeUtils';
import { getPathSegmentIndices } from '../utils/mapUtils';
import { calculateSectionTiming, getSmoothAnimationData } from '../utils/animationUtils';
import { CanvasColors } from '../constants/colors';

// G-circle dot size constants
const DOT_RADIUS = 6; // Main animation dots (increased from 4)
const TRAIL_DOT_RADIUS = 3; // Trail dots (unchanged)
const LEGEND_DOT_SIZE = DOT_RADIUS * 2; // Legend dots (diameter = radius * 2)

interface SectionAnalysis {
  start: number;
  end: number;
  time_lost: number;
  description: string;
}

interface SectionAnalysisPopupProps {
  section: SectionAnalysis;
  lapData: any; // TODO: Type this properly
  onClose: () => void;
  isPlaying: boolean;
  currentFrame: number; // Animation frame from main controller
  resetKey?: number; // Optional key to force animation reset
  hasOptimal?: boolean; // Whether optimal data is available (default true)
}

const SectionAnalysisPopup: React.FC<SectionAnalysisPopupProps> = ({
  section,
  lapData,
  onClose,
  isPlaying,
  currentFrame,
  resetKey = 0,
  hasOptimal = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationIdRef = useRef<number | null>(null);

  // Reset animation state when section changes OR resetKey changes
  useEffect(() => {
    // Cancel any existing animation
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
  }, [section, resetKey]);

  // Calculate section indices using the same method as LapPathVisualization
  const { startIdx, endIdx } = React.useMemo(() => {
    if (!lapData.displacement || lapData.displacement.length === 0) return { startIdx: 0, endIdx: 0 };
    
    // Use the same getPathSegmentIndices function as the main map
    const { startIndex, endIndex } = getPathSegmentIndices(
      section.start, 
      section.end, 
      lapData.displacement
    );
    
    return { startIdx: startIndex, endIdx: endIndex };
  }, [section.start, section.end, lapData.displacement]);

  // Calculate section timing using shared utility
  const animationTiming = React.useMemo(() => {
    return calculateSectionTiming(startIdx, endIdx, lapData);
  }, [startIdx, endIdx, lapData]);

  // Calculate current lap time within section
  const getCurrentLapTime = () => {
    if (animationTiming.actualRelativeTime.length === 0) return 0;
    
    const progress = currentFrame / 100;
    const currentActualTime = progress * (animationTiming.actualRelativeTime[animationTiming.actualRelativeTime.length - 1] || 0);
    
    return animationTiming.sectionData[0]?.time + currentActualTime;
  };

  // Stable refs for animation
  const animationTimingRef = useRef(animationTiming);
  
  // Update refs when values change
  useEffect(() => {
    animationTimingRef.current = animationTiming;
  }, [animationTiming]);

  const drawGCircle = useCallback((ctx: CanvasRenderingContext2D, frame: number) => {
    const canvas = ctx.canvas;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(canvas.width, canvas.height) * 0.45;
    
    // Calculate max G in this section for proper scaling
    const sectionLatAccel = animationTiming.sectionData.map(d => Math.abs(d.lat_accel));
    const sectionLongAccel = animationTiming.sectionData.map(d => Math.abs(d.long_accel));

    const maxG = Math.max(
      Math.max(...sectionLatAccel, 0),
      Math.max(...sectionLongAccel, 0),
      ...(hasOptimal ? [
        Math.max(...animationTiming.sectionData.map(d => Math.abs(d.optimal_lat_accel)), 0),
        Math.max(...animationTiming.sectionData.map(d => Math.abs(d.optimal_long_accel)), 0),
      ] : []),
      0.5 // minimum scale
    );
    
    // Determine increment and scale based on data magnitude
    let increment: number, scaleMaxG: number;
    if (maxG <= 2.0) {
      increment = 0.5;
      scaleMaxG = Math.ceil(maxG / increment) * increment;
    } else {
      increment = 1.0;
      scaleMaxG = Math.ceil(maxG / increment) * increment;
    }
    
    // Ensure minimum scale
    if (scaleMaxG < 0.5) scaleMaxG = 0.5;
    
    // Clear canvas
    ctx.fillStyle = CanvasColors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw G-circle (friction circle) - outer boundary
    ctx.strokeStyle = CanvasColors.gCircle;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();
    
    // Draw G-circle markers with dynamic increments
    ctx.strokeStyle = CanvasColors.gCircleMarkers;
    ctx.lineWidth = 1;
    for (let g = increment; g < scaleMaxG; g += increment) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * (g / scaleMaxG), 0, 2 * Math.PI);
      ctx.stroke();
    }
    
    // Draw center lines
    ctx.strokeStyle = CanvasColors.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, canvas.height);
    ctx.moveTo(0, centerY);
    ctx.lineTo(canvas.width, centerY);
    ctx.stroke();
    
    // Get current animation data using shared logic
    const currentAnimationData = getSmoothAnimationData(frame, animationTiming);
    
    if (animationTiming.sectionData.length === 0) return; // No data in this section
    
    // Scale acceleration values to pixel coordinates using section-specific scale
    const actualX = centerX + (currentAnimationData.actual.lat_accel / scaleMaxG * radius);
    const actualY = centerY - (currentAnimationData.actual.long_accel / scaleMaxG * radius);
    const optimalX = centerX + (currentAnimationData.optimal.lat_accel / scaleMaxG * radius);
    const optimalY = centerY - (currentAnimationData.optimal.long_accel / scaleMaxG * radius);
      
    // Draw fading trail from upsampled data (2-second window behind current position)
    const upsampledActual = animationTiming.upsampledActualData;
    const upsampledOptimal = animationTiming.upsampledOptimalData;
    if (upsampledActual.length >= 2) {
      const TRAIL_DURATION = 2.0; // seconds
      const totalDuration = animationTiming.maxSectionDuration / 1000;
      const sampleRate = (upsampledActual.length - 1) / totalDuration;
      const trailCount = Math.max(Math.round(sampleRate * TRAIL_DURATION), 10);
      const progress = frame / 100;
      const currentIdx = Math.min(Math.round(progress * (upsampledActual.length - 1)), upsampledActual.length - 1);
      const trailStart = Math.max(0, currentIdx - trailCount);

      for (let i = trailStart; i < currentIdx; i++) {
        const age = (currentIdx - i) / trailCount; // 0 = newest, 1 = oldest
        const opacity = 0.15 + 0.25 * (1.0 - age);

        const point = upsampledActual[i];
        const trailActualX = centerX + (point.lat_accel / scaleMaxG * radius);
        const trailActualY = centerY - (point.long_accel / scaleMaxG * radius);
        ctx.fillStyle = `rgba(239, 68, 68, ${opacity})`;
        ctx.beginPath();
        ctx.arc(trailActualX, trailActualY, TRAIL_DOT_RADIUS, 0, 2 * Math.PI);
        ctx.fill();

        if (hasOptimal) {
          const optPoint = upsampledOptimal[i];
          const trailOptimalX = centerX + (optPoint.lat_accel / scaleMaxG * radius);
          const trailOptimalY = centerY - (optPoint.long_accel / scaleMaxG * radius);
          ctx.fillStyle = `rgba(16, 185, 129, ${opacity})`;
          ctx.beginPath();
          ctx.arc(trailOptimalX, trailOptimalY, TRAIL_DOT_RADIUS, 0, 2 * Math.PI);
          ctx.fill();
        }
      }
    }

    if (hasOptimal) {
      // Draw connection line
      ctx.strokeStyle = CanvasColors.difference;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(actualX, actualY);
      ctx.lineTo(optimalX, optimalY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw optimal point (green) with darker green outline
      ctx.fillStyle = CanvasColors.optimal;
      ctx.strokeStyle = '#059669';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(optimalX, optimalY, DOT_RADIUS, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }

    // Draw actual point (red) with darker red outline
    ctx.fillStyle = CanvasColors.actual;
    ctx.strokeStyle = '#b91c1c'; // Darker red shade
    ctx.lineWidth = 2; // Thicker line for better visibility
    ctx.beginPath();
    ctx.arc(actualX, actualY, DOT_RADIUS, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    
    // Draw axis labels with proper directions
    ctx.fillStyle = CanvasColors.textSecondary;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    
    // X-axis labels (lateral acceleration) - above horizontal
    ctx.fillText('Right', centerX + radius - 15, centerY - 10);
    ctx.fillText('Left', centerX - radius + 15, centerY - 10);
    
    // Y-axis labels (longitudinal acceleration) - no rotation
    ctx.fillText('Brake', centerX - 15, centerY - radius + 15);
    ctx.fillText('Accel', centerX - 15, centerY + radius - 5);
    
    // Draw G unit labels for each circle - below horizontal
    ctx.textAlign = 'center';
    ctx.fillText(`${scaleMaxG}G`, centerX + radius - 15, centerY + 15);
    
    // Draw labels for inner circles - just numbers
    for (let g = increment; g < scaleMaxG; g += increment) {
      const circleRadius = radius * (g / scaleMaxG);
      ctx.fillText(`${g}`, centerX + circleRadius - 15, centerY + 15);
    }
  }, [animationTiming, hasOptimal]);

  // Draw G-circle when frame changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !lapData.lat_accel || !lapData.long_accel) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    drawGCircle(ctx, currentFrame);
  }, [currentFrame, lapData.lat_accel, lapData.long_accel, drawGCircle]);


  const hasAccelData = lapData.lat_accel && lapData.long_accel && (hasOptimal ? (lapData.optimal_lat_accel && lapData.optimal_long_accel) : true);
  
  // Portrait/landscape detection - this drives ALL layout decisions
  const isPortrait = window.innerHeight > window.innerWidth;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'var(--surface)',
        color: 'white',
        padding: '16px',
        display: 'flex',
        // CORE RULE: Portrait = horizontal layout, Landscape = vertical layout
        flexDirection: isPortrait ? 'row' : 'column',
        // ALIGNMENT RULES:
        // Portrait: align to start (left-justified), smaller gap
        // Landscape: center alignment
        alignItems: isPortrait ? 'flex-start' : 'center',
        justifyContent: isPortrait ? 'flex-start' : 'center',
        gap: isPortrait ? '12px' : '8px',
        overflow: 'hidden', // No scroll on main container
        boxSizing: 'border-box'
      }}
    >
      {/* SCROLLABLE CONTAINER for G-circle and text only */}
      <div style={{
        display: 'flex',
        flexDirection: isPortrait ? 'row' : 'column',
        alignItems: isPortrait ? 'flex-start' : 'center',
        justifyContent: isPortrait ? 'flex-start' : 'flex-start',
        gap: isPortrait ? '12px' : '8px',
        width: '100%',
        height: '100%',
        // SCROLL RULES - only on this inner container:
        // Portrait: horizontal scroll when G-circle + text don't fit
        // Landscape: vertical scroll when G-circle + text don't fit
        overflowX: isPortrait ? 'auto' : 'hidden',
        overflowY: isPortrait ? 'hidden' : 'auto',
        minWidth: 0,
        minHeight: 0
      }}>
      {hasAccelData && (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          // G-CIRCLE ALIGNMENT:
          // Portrait: left-align the G-circle within its container
          // Landscape: center-align as before
          alignItems: isPortrait ? 'flex-start' : 'center', 
          gap: '4px',
          // G-CIRCLE CONTAINER SIZING RULES:
          // Portrait: Take all available height, constrained width
          // Landscape: Take all available width, constrained height
          flex: isPortrait ? '0 0 auto' : '0 0 auto',
          width: isPortrait ? 'auto' : '100%',
          height: isPortrait ? '100%' : 'auto',
          // MAX SIZE CONSTRAINTS to prevent G-circle from getting too huge:
          maxWidth: isPortrait ? '70vh' : '300px', // Portrait: limit by viewport height, Landscape: max 500px
          maxHeight: isPortrait ? '300px' : '70vh', // Portrait: max 400px, Landscape: limit by viewport height
          minWidth: 0,
          minHeight: 0
        }}>
          {/* SQUARE CONTAINER for G-circle and all its overlaid elements */}
          <div style={{ 
            position: 'relative',
            // SQUARE LOGIC: Always maintain 1:1 aspect ratio
            // Portrait: height drives the size (limited by container height)
            // Landscape: width drives the size (limited by container width)
            width: isPortrait ? 'auto' : '100%',
            height: isPortrait ? '100%' : 'auto',
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <canvas
              ref={canvasRef}
              width={300}
              height={300}
              style={{
                border: `1px solid ${CanvasColors.border}`,
                borderRadius: '8px',
                backgroundColor: CanvasColors.background,
                // Canvas fills the square container
                width: '100%',
                height: '100%',
                display: 'block'
              }}
            />
            {/* Lap time - always visible in top right */}
            <div style={{ 
              position: 'absolute',
              top: '8px',
              right: '8px',
              color: '#fbbf24', 
              fontWeight: '600', 
              fontSize: '11px',
              fontFamily: 'monospace',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: '4px 8px',
              borderRadius: '4px',
              zIndex: 10
            }}>
              {formatTime(getCurrentLapTime() || lapData.lap_time || 0, 2)}
            </div>
            
            {/* Legend - moved to bottom left inside G-circle */}
            <div style={{ 
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              display: 'flex', 
              flexDirection: 'column',
              gap: '4px',
              fontSize: '10px',
              zIndex: 10
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ 
                  width: `${LEGEND_DOT_SIZE}px`, 
                  height: `${LEGEND_DOT_SIZE}px`, 
                  backgroundColor: '#ef4444', 
                  borderRadius: '50%' 
                }}></div>
                <span style={{ color: '#9ca3af' }}>Actual</span>
              </div>
              {hasOptimal && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{
                    width: `${LEGEND_DOT_SIZE}px`,
                    height: `${LEGEND_DOT_SIZE}px`,
                    backgroundColor: '#10b981',
                    borderRadius: '50%'
                  }}></div>
                  <span style={{ color: '#9ca3af' }}>Optimal</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* ANALYSIS TEXT with portrait/landscape logic */}
      {section.description && <div style={{
        // TEXT CONTAINER SIZING RULES:
        // Portrait: Use remaining space after G-circle, flexible height
        // Landscape: Full width, takes remaining vertical space
        flex: '1 1 auto',
        width: isPortrait ? 'auto' : '100%',
        height: 'auto',
        maxWidth: isPortrait ? 'none' : '100%',
        minWidth: 0,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        // TEXT ALIGNMENT RULES:
        // Portrait: left-justified (start of container)
        // Landscape: top-justified (start of container) 
        justifyContent: 'flex-start',
        alignItems: isPortrait ? 'flex-start' : 'center'
      }}>
        <p
          style={{
            margin: 0,
            fontSize: isPortrait ? '13px' : '14px',
            lineHeight: '1.4',
            color: '#f3f4f6',
            textAlign: isPortrait ? 'left' : 'center',
            overflow: isPortrait ? 'visible' : 'hidden',
            wordWrap: 'break-word'
          }}
        >
          {section.description}
        </p>
      </div>}
      </div> {/* Close scrollable container */}
    </div>
  );
};

export default SectionAnalysisPopup;
